/**
 * Chooses between the VideoJS player and the live-stream placeholder, and
 * wires the player's error/xhr callbacks to the live-stream recovery logic
 * (resume polling + arm autoplay for go-live).
 *
 * Shared by the media page block and the embed player so both stay in sync
 * on live-stream behaviour (start/end messaging, active polling, autoplay-
 * on-go-live, and the live-not-active error recovery) instead of each
 * re-implementing the same decision tree.
 *
 * @module components/manage/Blocks/VideoJS/LiveAwareVideoPlayer
 */

import Body from 'volto-base-addon/components/manage/VideoJS/Body';
import VideoPlaceholder from 'volto-base-addon/components/manage/Blocks/VideoJS/VideoPlaceholder';

/**
 * @param {Object} props
 * @param {Object} props.live - Return value of `useLiveStream`.
 * @param {Object|null} props.playerData - Pre-resolved `data` for `Body`
 *   (already run through `live.applyLiveOptions`), or `null`/falsy while no
 *   playable source is available yet.
 * @param {Object} props.bodyProps - Remaining props to forward to `Body`
 *   (parentContent, parentUid, parentItems, startTime, titleBar, editMode…).
 */
const LiveAwareVideoPlayer = ({ live, playerData, bodyProps }) => {
  const canPlay = !!playerData && (!live.isLiveStream || live.isLiveActive);

  if (canPlay) {
    return (
      <Body
        {...bodyProps}
        data={playerData}
        onError={live.handlePlayerError}
        onXhrResponse={live.handleXhrResponse}
      />
    );
  }

  if (live.isLiveStream) {
    return <VideoPlaceholder>{live.getPlaceholderMessage()}</VideoPlaceholder>;
  }

  return null;
};

export default LiveAwareVideoPlayer;
